/* eslint-disable react/forbid-prop-types, react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import {
  MdFlight,
  MdCheckCircle,
  MdInfoOutline,
  MdRefresh,
} from 'react-icons/md';
import { IoBag } from 'react-icons/io5';
import AppSidebar from '../app-sidebar/AppSidebar';
import AppButton from '../app-button/AppButton';
import AppFlightLogo from '../app-flight-logo/AppFlightLogo';
import {
  formatCurrency,
  getLegBaggage,
  getBaggage,
} from '../../helper-utils/appUtils';
import './ClassAvailabilitySidebar.scss';

const getSeatCountClassName = (count) => {
  const parsedCount = parseInt(count, 10);
  if (count === '9' || (!Number.isNaN(parsedCount) && parsedCount >= 5)) {
    return 'seats-plentiful';
  }
  if (!Number.isNaN(parsedCount) && parsedCount > 0 && parsedCount < 5) {
    return 'seats-low';
  }
  return 'seats-closed';
};

function ClassAvailabilitySidebar({
  isOpen,
  onClose,
  availClassList = [],
  selectedClasses = [],
  onClassSelect,
  onFetchFare,
  isFareFetched = false,
  repriceResponse = null,
  journeyType = 'oneway',
  onClassSelectForRoundtrip,
  onNavigateToBookingPage,
  isLoading = false,
}) {
  if (!isOpen || !availClassList || availClassList.length === 0) {
    return null;
  }

  const totalSectors = availClassList.length;
  const selectedCount = selectedClasses.filter(
    (sc) => sc && sc.selectedclass && sc.selectedclass !== '',
  ).length;
  const allSectorsSelected = totalSectors > 0 && selectedCount === totalSectors;

  const totalRepricedFare =
    repriceResponse?.flightjourneys?.[0]?.flightoptions?.[0]
      ?.recommendedflight?.[0]?.flightfare?.totalfq;

  const baggageLegs = repriceResponse?.flightjourneys?.[0]?.flightoptions?.[0]
    ?.recommendedflight?.[0]
    ? repriceResponse.flightjourneys[0].flightoptions[0].recommendedflight.flatMap(
        (fl) => fl?.flightlegs || [],
      )
    : [];

  return (
    <AppSidebar
      sidebarTitle="Class Availability"
      className="flight-details-sidebar class-avail-sidebar"
      animatedClass="slide-from-right-animation"
      onHideSidebar={onClose}
    >
      <div className="class-avail-container">
        {/* Header Context Banner */}
        <div className="class-avail-header-banner">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <div className="class-avail-header-title">
                <MdFlight style={{ transform: 'rotate(90deg)' }} />
                Select Booking Class
              </div>
              <div className="class-avail-header-subtitle">
                Select your desired RBD/class for each sector to check fare
                availability.
              </div>
            </div>
            <div
              className={`class-avail-progress-pill ${
                allSectorsSelected ? 'complete' : ''
              }`}
            >
              {allSectorsSelected ? (
                <>
                  <MdCheckCircle className="text-success" />
                  All {totalSectors} Sectors Selected
                </>
              ) : (
                <>
                  <MdInfoOutline className="text-warning" />
                  {selectedCount} of {totalSectors} Selected
                </>
              )}
            </div>
          </div>
        </div>

        {/* Sectors List */}
        <div className="class-avail-sectors-list">
          {availClassList.map((resultRow, sectorIdx) => {
            const currentSelected = selectedClasses[sectorIdx]?.selectedclass;
            const isSectorChosen = Boolean(currentSelected);

            return (
              <div
                key={`${resultRow.carrier || ''}-${
                  resultRow.flightnumber || ''
                }-${resultRow.tripNum || sectorIdx}`}
                className="class-avail-sector-card"
              >
                {/* Sector Header */}
                <div className="class-avail-sector-header">
                  <div className="d-flex align-items-center gap-2">
                    {resultRow.carrier && (
                      <div className="class-avail-airline-logo">
                        <AppFlightLogo
                          carrier={resultRow.carrier.substring(0, 2)}
                        />
                      </div>
                    )}
                    <div>
                      <div className="class-avail-route">
                        <span>{resultRow.origin}</span>
                        <MdFlight className="class-avail-flight-icon" />
                        <span>{resultRow.destination}</span>
                      </div>
                      <div className="class-avail-meta">
                        {resultRow.carrier && resultRow.flightnumber && (
                          <span>
                            {resultRow.carrier}-{resultRow.flightnumber}
                          </span>
                        )}
                        {resultRow.tripNum && (
                          <span className="ms-1">
                            • Sector {resultRow.tripNum}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    {isSectorChosen ? (
                      <span className="class-avail-badge success">
                        <MdCheckCircle className="me-1" />
                        Selected: <strong>{currentSelected}</strong>
                      </span>
                    ) : (
                      <span className="class-avail-badge pending">
                        <MdInfoOutline className="me-1" />
                        Select Class
                      </span>
                    )}
                  </div>
                </div>

                {/* Class Matrix Grid */}
                <div className="class-avail-grid-wrapper">
                  <div className="class-avail-grid">
                    {resultRow.availclasses?.map((availClass, classIdx) => {
                      const { class: rbdClass, count, cabinClass } = availClass;
                      const isSelected = currentSelected === rbdClass;
                      const seatBadgeClass = getSeatCountClassName(count);

                      return (
                        <button
                          type="button"
                          key={`${rbdClass}-${classIdx}`}
                          onClick={() =>
                            onClassSelect(availClass, sectorIdx, resultRow)
                          }
                          className={`class-avail-chip ${
                            isSelected ? 'selected' : ''
                          }`}
                          title={`${rbdClass} - ${count} seats${
                            cabinClass ? ` (${cabinClass})` : ''
                          }`}
                        >
                          <div className="class-avail-chip-top">
                            <span className="class-code">{rbdClass}</span>
                            {isSelected && (
                              <MdCheckCircle className="class-check-icon" />
                            )}
                          </div>
                          <div className="class-avail-chip-bottom">
                            <span
                              className={`seat-count-tag ${seatBadgeClass}`}
                            >
                              {count}
                            </span>
                            {cabinClass && (
                              <span className="cabin-name" title={cabinClass}>
                                {cabinClass}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Actions / Repricing Card */}
        <div className="class-avail-footer-card">
          {!isFareFetched || !repriceResponse ? (
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="class-avail-footer-hint">
                {allSectorsSelected ? (
                  <span className="text-success fw-500">
                    <MdCheckCircle className="me-1" /> All sectors selected.
                    Ready to fetch fare.
                  </span>
                ) : (
                  <span className="text-muted">
                    <MdInfoOutline className="me-1" /> Select a class for every
                    sector above.
                  </span>
                )}
              </div>
              <AppButton
                btnText={isLoading ? 'Fetching Fare...' : 'Fetch Fare'}
                onClick={onFetchFare}
                variant="primary"
                disabled={isLoading}
              />
            </div>
          ) : (
            <div className="class-avail-reprice-card">
              <div className="class-avail-reprice-header">
                <span className="badge-reprice-tag">
                  <MdCheckCircle /> Fare Updated
                </span>
                <button
                  type="button"
                  className="btn btn-link btn-sm text-decoration-none p-0 text-secondary"
                  onClick={onFetchFare}
                  disabled={isLoading}
                >
                  <MdRefresh className="me-1" />
                  {isLoading ? 'Fetching...' : 'Re-fetch Fare'}
                </button>
              </div>

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-1">
                {/* Baggage tags */}
                <div className="d-flex gap-2 align-items-center flex-wrap">
                  {baggageLegs.map((leg, lIdx) => {
                    const bagText = getLegBaggage(leg?.bagweight, leg?.bagunit);
                    if (!bagText) return null;
                    return (
                      <span
                        key={`${leg?.origin}-${leg?.destination}-${lIdx}`}
                        className="class-avail-baggage-tag"
                        title={getBaggage(leg?.bagweight, leg?.bagunit)}
                      >
                        <IoBag className="text-secondary me-1" />
                        <strong>
                          {leg?.origin}-{leg?.destination}:
                        </strong>
                        <span className="ms-1">{bagText}</span>
                      </span>
                    );
                  })}
                </div>

                {/* Price & Primary CTA */}
                <div className="d-flex align-items-center gap-3 ms-auto">
                  {totalRepricedFare !== undefined && (
                    <div className="text-end">
                      <div className="class-avail-price-label">Total Fare</div>
                      <div className="class-avail-price-val">
                        {formatCurrency(totalRepricedFare)}
                      </div>
                    </div>
                  )}

                  {journeyType?.toLowerCase() === 'roundtrip' &&
                  onClassSelectForRoundtrip ? (
                    <AppButton
                      btnText="Select Fare"
                      variant="primary"
                      onClick={onClassSelectForRoundtrip}
                    />
                  ) : (
                    <AppButton
                      btnText="Book Now"
                      variant="primary"
                      onClick={onNavigateToBookingPage}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppSidebar>
  );
}

ClassAvailabilitySidebar.defaultProps = {
  repriceResponse: null,
  journeyType: 'oneway',
  onClassSelectForRoundtrip: undefined,
  onNavigateToBookingPage: undefined,
  isLoading: false,
  isFareFetched: false,
};

ClassAvailabilitySidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  availClassList: PropTypes.array.isRequired,
  selectedClasses: PropTypes.array.isRequired,
  onClassSelect: PropTypes.func.isRequired,
  onFetchFare: PropTypes.func.isRequired,
  isFareFetched: PropTypes.bool,
  repriceResponse: PropTypes.object,
  journeyType: PropTypes.string,
  onClassSelectForRoundtrip: PropTypes.func,
  onNavigateToBookingPage: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default ClassAvailabilitySidebar;
